import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isTransactable, listAccounts, createAccount } from '@/api/accounts'
import {
  recordLoanIncrease,
  recordLoanPayment,
  updateLoan,
  type CreateLoanPayload,
  type LoanDetail,
} from '@/api/loans'
import { markDone } from '@/api/recurring'
import { DatePicker } from '@/components/DatePicker'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { today } from '@/lib/transactionDateRangeFilter'
import { MODAL_BOX, MODAL_OVERLAY } from '@/components/transactions/TransactionForms'

const LABEL_CLS = 'block text-sm font-medium text-[var(--fl-text-1)] mb-1'
const HINT_CLS = 'mt-1 text-xs text-[var(--fl-text-2)]'
const ERROR_CLS = 'mt-1 text-xs text-[var(--fl-error)]'

/** Paise from a typed amount; null when blank, NaN when not a number. */
function parsePaise(text: string): number | null {
  const trimmed = text.trim()
  if (!trimmed) return null
  const n = Number(trimmed)
  return Number.isNaN(n) ? NaN : toPaise(n)
}

function apiError(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
  return detail ?? 'Something went wrong. Please try again.'
}

function invalidateLoan(queryClient: ReturnType<typeof useQueryClient>, loanId?: string) {
  if (loanId) queryClient.invalidateQueries({ queryKey: ['loan', loanId] })
  for (const key of ['accounts', 'transactions', 'dashboard', 'budget', 'recurring'])
    queryClient.invalidateQueries({ queryKey: [key] })
}

function useTransactableAccounts() {
  const { data } = useQuery({ queryKey: ['accounts'], queryFn: () => listAccounts() })
  return (data?.accounts ?? []).filter((a) => isTransactable(a.type))
}

function Modal({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={MODAL_OVERLAY}>
      <div className={MODAL_BOX} role="dialog" aria-label={title}>
        <h2 className="mb-4 text-lg font-bold text-[var(--fl-text-1)]">{title}</h2>
        {children}
      </div>
    </div>
  )
}

function AmountField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  error?: string | null
  hint?: string
}) {
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLS}>
        {label}
      </label>
      <Input id={id} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} />
      {hint && <p className={HINT_CLS}>{hint}</p>}
      {error && <p className={ERROR_CLS}>{error}</p>}
    </div>
  )
}

function AccountField({
  label,
  value,
  onChange,
  emptyLabel,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  emptyLabel: string
}) {
  const accounts = useTransactableAccounts()
  return (
    <div>
      <label className={LABEL_CLS}>{label}</label>
      <Select aria-label={label} value={value} onChange={onChange}>
        <option value="">{emptyLabel}</option>
        {accounts.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </Select>
    </div>
  )
}

function FormButtons({
  onCancel,
  onSave,
  disabled,
  pending,
}: {
  onCancel: () => void
  onSave: () => void
  disabled?: boolean
  pending: boolean
}) {
  return (
    <div className="flex gap-3 pt-2">
      <Button variant="outline" onClick={onCancel} className="flex-1">
        Cancel
      </Button>
      <Button onClick={onSave} disabled={disabled || pending} className="flex-1 font-semibold">
        {pending ? 'Saving…' : 'Save'}
      </Button>
    </div>
  )
}

export function AddLoanModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [lender, setLender] = useState('')
  const [outstanding, setOutstanding] = useState('')
  const [emi, setEmi] = useState('')
  const [emiAccountId, setEmiAccountId] = useState('')
  const [receivedIntoAccountId, setReceivedIntoAccountId] = useState('')
  const [date, setDate] = useState(today())
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (payload: CreateLoanPayload) => createAccount(payload),
    onSuccess: () => {
      invalidateLoan(queryClient)
      onClose()
    },
    onError: (err) => setSubmitError(apiError(err)),
  })

  function submit() {
    const next: Record<string, string> = {}
    if (!name.trim()) next.name = 'Enter a name for the loan'
    const outPaise = parsePaise(outstanding)
    if (outPaise === null || Number.isNaN(outPaise) || outPaise <= 0)
      next.outstanding = 'Enter an amount greater than ₹0'
    const emiPaise = parsePaise(emi)
    if (emiPaise !== null && (Number.isNaN(emiPaise) || emiPaise <= 0))
      next.emi = 'Enter an amount greater than ₹0, or leave it blank'
    setErrors(next)
    if (Object.keys(next).length > 0) return
    setSubmitError(null)
    mutation.mutate({
      type: 'LOAN',
      name: name.trim(),
      ...(lender.trim() ? { lender: lender.trim() } : {}),
      outstanding: fromPaise(outPaise!),
      ...(emiPaise ? { emiAmount: fromPaise(emiPaise) } : {}),
      ...(emiAccountId ? { emiAccountId } : {}),
      ...(receivedIntoAccountId ? { receivedIntoAccountId, startDate: date } : {}),
    })
  }

  return (
    <Modal title="Add loan">
      <div className="space-y-4">
        <div>
          <label htmlFor="loan-name" className={LABEL_CLS}>
            Name
          </label>
          <Input
            id="loan-name"
            value={name}
            maxLength={50}
            placeholder="e.g. Home loan – SBI"
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <p className={ERROR_CLS}>{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="loan-lender" className={LABEL_CLS}>
            Lender (optional)
          </label>
          <Input id="loan-lender" value={lender} onChange={(e) => setLender(e.target.value)} />
        </div>
        <AmountField
          id="loan-outstanding"
          label="Outstanding principal today"
          value={outstanding}
          onChange={setOutstanding}
          error={errors.outstanding}
          hint="From your latest loan statement or the bank app."
        />
        <AccountField
          label="Received into account"
          value={receivedIntoAccountId}
          onChange={setReceivedIntoAccountId}
          emptyLabel="No account — the money didn't come to me"
        />
        {receivedIntoAccountId ? (
          <div>
            <label className={LABEL_CLS}>Date received</label>
            <DatePicker value={date} onChange={setDate} />
          </div>
        ) : (
          <p className={`${HINT_CLS} -mt-2`}>
            Leave blank for a loan you&rsquo;re already paying, or one paid straight to a builder or
            dealer.
          </p>
        )}
        <AmountField
          id="loan-emi"
          label="EMI amount (optional)"
          value={emi}
          onChange={setEmi}
          error={errors.emi}
        />
        <AccountField
          label="EMI usually paid from (optional)"
          value={emiAccountId}
          onChange={setEmiAccountId}
          emptyLabel="Not set"
        />
        <p className={HINT_CLS}>The EMI amount and account only pre-fill the Pay EMI form.</p>
        {submitError && <p className={ERROR_CLS}>{submitError}</p>}
        <FormButtons onCancel={onClose} onSave={submit} pending={mutation.isPending} />
      </div>
    </Modal>
  )
}

export function LoanPaymentModal({
  loan,
  type,
  recurring,
  onClose,
}: {
  loan: LoanDetail
  type: 'EMI' | 'PREPAYMENT'
  // Marking a loan-linked EMI reminder paid: same form, posted to the reminder's mark-done
  // so its due date advances with the payment.
  recurring?: { itemId: string; dueDate: string; accountId?: string; emiAmount: number }
  onClose: () => void
}) {
  const { format } = useFormatCurrency()
  const queryClient = useQueryClient()
  const lastEmi = loan.entries.find((e) => e.type === 'EMI')
  const [accountId, setAccountId] = useState(
    recurring?.accountId ?? loan.emiAccountId ?? lastEmi?.accountId ?? ''
  )
  const [date, setDate] = useState(recurring?.dueDate ?? today())
  // Pre-filled from the last EMI's split; the user corrects it from the bank statement.
  const [principal, setPrincipal] = useState(
    type === 'EMI' && lastEmi ? String(lastEmi.principalAmount) : ''
  )
  const [interest, setInterest] = useState(
    type === 'EMI' && lastEmi ? String(lastEmi.interestAmount) : ''
  )
  const [amountError, setAmountError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async (): Promise<void> => {
      if (recurring) {
        await markDone(recurring.itemId, {
          date,
          fromAccountId: accountId,
          principal: fromPaise(parsePaise(principal) ?? 0),
          interest: fromPaise(parsePaise(interest) ?? 0),
        })
        return
      }
      await recordLoanPayment(loan.id, {
        type,
        accountId,
        date,
        principalAmount: fromPaise(parsePaise(principal) ?? 0),
        ...(type === 'EMI' ? { interestAmount: fromPaise(parsePaise(interest) ?? 0) } : {}),
      })
    },
    onSuccess: () => {
      invalidateLoan(queryClient, loan.id)
      onClose()
    },
    onError: (err) => setSubmitError(apiError(err)),
  })

  const principalPaise = parsePaise(principal) ?? 0
  const interestPaise = type === 'EMI' ? (parsePaise(interest) ?? 0) : 0
  const totalPaise = principalPaise + interestPaise

  function submit() {
    setSubmitError(null)
    if (
      Number.isNaN(principalPaise) ||
      Number.isNaN(interestPaise) ||
      principalPaise < 0 ||
      interestPaise < 0
    ) {
      setAmountError('Enter valid amounts')
      return
    }
    if (totalPaise <= 0) {
      setAmountError(
        type === 'EMI' ? 'Enter the principal or interest paid' : 'Enter the amount prepaid'
      )
      return
    }
    if (principalPaise > toPaise(loan.outstanding)) {
      setAmountError(`Principal is more than the outstanding of ${format(loan.outstanding)}`)
      return
    }
    setAmountError(null)
    mutation.mutate()
  }

  return (
    <Modal
      title={
        recurring
          ? `Mark EMI paid — ${loan.name}`
          : type === 'EMI'
            ? `Pay EMI — ${loan.name}`
            : `Prepay — ${loan.name}`
      }
    >
      <div className="space-y-4">
        {type === 'EMI' ? (
          <div className="grid grid-cols-2 gap-3">
            <AmountField
              id="pay-principal"
              label="Principal"
              value={principal}
              onChange={setPrincipal}
            />
            <AmountField
              id="pay-interest"
              label="Interest"
              value={interest}
              onChange={setInterest}
            />
          </div>
        ) : (
          <AmountField
            id="pay-principal"
            label="Amount (principal only)"
            value={principal}
            onChange={setPrincipal}
          />
        )}
        {amountError && <p className={`${ERROR_CLS} -mt-2`}>{amountError}</p>}
        {recurring && (
          <p className={`${HINT_CLS} -mt-2`}>
            {lastEmi
              ? 'Same as last time — change if your statement differs. '
              : `Your EMI is ${format(recurring.emiAmount)} — how much was principal? `}
            {totalPaise > 0 &&
            !Number.isNaN(totalPaise) &&
            totalPaise !== toPaise(recurring.emiAmount)
              ? `${format(fromPaise(Math.abs(totalPaise - toPaise(recurring.emiAmount))))} ${
                  totalPaise > toPaise(recurring.emiAmount) ? 'more' : 'less'
                } than your usual EMI.`
              : ''}
          </p>
        )}
        {!recurring && type === 'EMI' && (
          <p className={`${HINT_CLS} -mt-2`}>
            {totalPaise > 0 && !Number.isNaN(totalPaise)
              ? `Total ${format(fromPaise(totalPaise))}. `
              : ''}
            {loan.emiAmount != null ? `Your EMI is ${format(loan.emiAmount)}. ` : ''}
            Split as shown on your loan statement or bank SMS.
          </p>
        )}
        <AccountField
          label="Paid from"
          value={accountId}
          onChange={setAccountId}
          emptyLabel="Select account"
        />
        <div>
          <label className={LABEL_CLS}>Date</label>
          <DatePicker value={date} onChange={setDate} />
        </div>
        {submitError && <p className={ERROR_CLS}>{submitError}</p>}
        <FormButtons
          onCancel={onClose}
          onSave={submit}
          disabled={accountId === ''}
          pending={mutation.isPending}
        />
      </div>
    </Modal>
  )
}

export function LoanIncreaseModal({ loan, onClose }: { loan: LoanDetail; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [amount, setAmount] = useState('')
  const [accountId, setAccountId] = useState('')
  const [date, setDate] = useState(today())
  const [amountError, setAmountError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (paise: number) =>
      recordLoanIncrease(loan.id, {
        amount: fromPaise(paise),
        date,
        ...(accountId ? { accountId } : {}),
      }),
    onSuccess: () => {
      invalidateLoan(queryClient, loan.id)
      onClose()
    },
    onError: (err) => setSubmitError(apiError(err)),
  })

  function submit() {
    setSubmitError(null)
    const paise = parsePaise(amount)
    if (paise === null || Number.isNaN(paise) || paise <= 0) {
      setAmountError('Enter an amount greater than ₹0')
      return
    }
    setAmountError(null)
    mutation.mutate(paise)
  }

  return (
    <Modal title={`Increase outstanding — ${loan.name}`}>
      <div className="space-y-4">
        <AmountField
          id="increase-amount"
          label="Amount added to the loan"
          value={amount}
          onChange={setAmount}
          error={amountError}
          hint="A top-up, a further disbursement, or a charge added to the loan."
        />
        <AccountField
          label="Received into account (optional)"
          value={accountId}
          onChange={setAccountId}
          emptyLabel="No account — the money didn't come to me"
        />
        <div>
          <label className={LABEL_CLS}>Date</label>
          <DatePicker value={date} onChange={setDate} />
        </div>
        {submitError && <p className={ERROR_CLS}>{submitError}</p>}
        <FormButtons onCancel={onClose} onSave={submit} pending={mutation.isPending} />
      </div>
    </Modal>
  )
}

export function EditLoanModal({ loan, onClose }: { loan: LoanDetail; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [lender, setLender] = useState(loan.lender ?? '')
  const [emi, setEmi] = useState(loan.emiAmount != null ? String(loan.emiAmount) : '')
  const [emiAccountId, setEmiAccountId] = useState(loan.emiAccountId ?? '')
  const [emiError, setEmiError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (emiPaise: number | null) =>
      updateLoan(loan.id, {
        lender: lender.trim() || null,
        emiAmount: emiPaise === null ? null : fromPaise(emiPaise),
        emiAccountId: emiAccountId || null,
      }),
    onSuccess: () => {
      invalidateLoan(queryClient, loan.id)
      onClose()
    },
    onError: (err) => setSubmitError(apiError(err)),
  })

  function submit() {
    setSubmitError(null)
    const paise = parsePaise(emi)
    if (paise !== null && (Number.isNaN(paise) || paise <= 0)) {
      setEmiError('Enter an amount greater than ₹0, or leave it blank')
      return
    }
    setEmiError(null)
    mutation.mutate(paise)
  }

  return (
    <Modal title="Edit loan">
      <div className="space-y-4">
        <div>
          <label htmlFor="edit-lender" className={LABEL_CLS}>
            Lender (optional)
          </label>
          <Input id="edit-lender" value={lender} onChange={(e) => setLender(e.target.value)} />
        </div>
        <AmountField
          id="edit-emi"
          label="EMI amount (optional)"
          value={emi}
          onChange={setEmi}
          error={emiError}
        />
        <AccountField
          label="EMI usually paid from (optional)"
          value={emiAccountId}
          onChange={setEmiAccountId}
          emptyLabel="Not set"
        />
        <p className={HINT_CLS}>These only pre-fill the Pay EMI form.</p>
        {submitError && <p className={ERROR_CLS}>{submitError}</p>}
        <FormButtons onCancel={onClose} onSave={submit} pending={mutation.isPending} />
      </div>
    </Modal>
  )
}
